import { ChangeEvent, useMemo, useRef, useState } from "react";
import { isEmpty } from "lodash";

import "./Checklist.scss";

interface ChecklistChangeEvent {
    name: string;
    value: string[];
}

interface ChecklistItem {
    value: string;
    label: string;
}

interface ChecklistProps {
    name: string;
    title: string;
    values: string[];
    onChange: (event: ChecklistChangeEvent) => void;
    items: ChecklistItem[];
    searchable?: boolean;
    mandatory?: boolean;
    requiredMark?: boolean;
}

function Checklist(props: ChecklistProps): JSX.Element {
    const { searchable = false, mandatory = false, requiredMark = false, title, items, name, values, onChange } = props;

    const checkboxRef = useRef<HTMLInputElement>();

    const [term, setTerm] = useState("");

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;

        const newValues = [...values];

        newValues.includes(value)
            ? newValues.splice(newValues.indexOf(value), 1)
            : newValues.push(value);

        if (mandatory) {
            checkboxRef.current.indeterminate = (newValues.length > 0 && newValues.length < items.length);
        }

        onChange({
            name,
            value: newValues
        });
    };

    const isChecked = (value: string): boolean => values.includes(value);

    const handleParentChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { target } = e;
        const { name } = target;

        if (mandatory) {
            checkboxRef.current.indeterminate = false;
        }

        const newValues = (values.length !== items.length)
            ? items.map(({ value }) => value)
            : [];

        onChange({
            name,
            value: newValues
        });
    };

    const isParentChecked = useMemo(
        () => values.length === items.length && !!items.length,
        [items.length, values.length]
    );

    const checklistItemsClassName = useMemo(
        () => searchable
            ? "checklist-items checklist-items-searchable"
            : "checklist-items",
        [searchable]
    );

    return (
        <div className="checklist">
            <div className="checkbox">
                <label className={`checklist-header ${requiredMark ? "checklist-header-required" : ""}`}>
                    <input
                        ref={checkboxRef}
                        name={name}
                        type="checkbox"
                        onChange={handleParentChange}
                        checked={isParentChecked}
                    />

                    {title}
                </label>
            </div>

            {searchable && (
                <div className="form-group">
                    <input
                        value={term}
                        onChange={e => setTerm(e.target.value)}
                        type="search"
                        placeholder="Search..."
                        className="form-control"
                    />
                </div>
            )}

            <div className={checklistItemsClassName}>
                {items
                    .filter(item => {
                        if (isEmpty(term) || !searchable) return true;

                        return item.label
                            .toLowerCase()
                            .search(new RegExp(term.toLowerCase() || "", "gi")) > -1;
                    })
                    .map((item, index) => {
                        const id = `${title}-${index}`;

                        return (
                            <div key={id} className="checkbox">
                                <label
                                    htmlFor={id}
                                    title={item.label}
                                >
                                    <input
                                        id={id}
                                        name={name}
                                        type="checkbox"
                                        value={item.value}
                                        onChange={handleChange}
                                        checked={isChecked(item.value)}
                                    />

                                    {item.label}
                                </label>
                            </div>
                        );
                    })}
            </div>
        </div>
    );
}

export { ChecklistChangeEvent, ChecklistItem };

export default Checklist;
